import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import Button from '../../components/buttons/Button';
import ApiService from '../../services/ApiService';
import { toast } from 'react-toastify';
import { ErrorType } from '../../types';
interface ModalContentProps {
  closeModal: (reset: boolean) => void;
  startDate: Date | null;
  endDate: Date | null;
  lounges: string[];
  allLounge: [
    {
      name: string;
      id: string;
      location: string;
    },
  ];
  reportType: string;
}

const ModalContent: React.FC<ModalContentProps> = ({
  closeModal,
  startDate,
  endDate,
  lounges,
  allLounge,
  reportType,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        closeModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeModal]);

  const handleDownload = async () => {
    setLoading(true);

    try {
      const isVoucher = reportType === 'vouchers';
      const endpoint = isVoucher
        ? 'v1/voucher/voucherReport'
        : 'v1/lounge/transactions';
      const filename = `${isVoucher ? 'Redeem-Vouchers' : 'transactions'}-${
        startDate ? dayjs(startDate).format('DD-MM-YYYY') : ''
      }-${endDate ? dayjs(endDate).format('DD-MM-YYYY') : ''}.csv`;

      await ApiService.downloadFileBuffer(
        endpoint,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        filename,
        {
          excel: true,
          start_date: startDate ? dayjs(startDate).format('YYYY-MM-DD') : null,
          end_date: endDate ? dayjs(endDate).format('YYYY-MM-DD') : null,
          lounge_ids: lounges,
          ...(isVoucher ? {} : { report_type: reportType }),
        },
      );

      closeModal(true);
    } catch (err: unknown) {
      console.error(err);
      let errorMessage = 'Error downloading file';

      try {
        const error = err as ErrorType;
        if (error?.response?.data) {
          // Check if response data is an ArrayBuffer
          if (error.response.data instanceof ArrayBuffer) {
            const decoder = new TextDecoder();
            const text = decoder.decode(error.response.data);
            const parsed = JSON.parse(text);
            // Check for both possible error message formats
            errorMessage =
              parsed?.message || parsed?.error?.message || errorMessage;
          } else {
            errorMessage = error.response.data.error?.message || errorMessage;
          }
        }
      } catch (parseError) {
        console.error('Failed to parse error message', parseError);
      }

      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const loungeNames =
    lounges
      ?.map((item) => allLounge.find((option) => option.id === item)?.name)
      .join(', ') || '';

  const displayNames =
    loungeNames.length > 500 ? loungeNames.slice(0, 500) + '...' : loungeNames;

  return (
    <div
      ref={modalRef}
      className="relative flex h-[464px] w-[856px] flex-col rounded-[12px] border bg-white"
      style={{ border: '1px solid rgba(229, 231, 235, 1)' }}
    >
      <div
        className="absolute right-4 top-4 cursor-pointer"
        onClick={() => closeModal(false)}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.61317 8L15.6517 1.96146C15.7607 1.85622 15.8476 1.73033 15.9074 1.59114C15.9672 1.45195 15.9986 1.30225 16 1.15077C16.0013 0.999286 15.9724 0.849059 15.915 0.708852C15.8577 0.568645 15.773 0.441266 15.6658 0.334148C15.5587 0.22703 15.4314 0.142318 15.2911 0.0849552C15.1509 0.0275921 15.0007 -0.00127326 14.8492 4.30753e-05C14.6978 0.00135941 14.548 0.0328313 14.4089 0.0926224C14.2697 0.152413 14.1438 0.239326 14.0385 0.34829L8 6.38683L1.96146 0.34829C1.74629 0.140474 1.45811 0.0254816 1.15898 0.0280809C0.859851 0.0306803 0.57371 0.150663 0.362187 0.362187C0.150663 0.57371 0.0306803 0.859851 0.0280809 1.15898C0.0254816 1.45811 0.140474 1.74629 0.34829 1.96146L6.38683 8L0.34829 14.0385C0.239326 14.1438 0.152413 14.2697 0.0926224 14.4089C0.0328313 14.548 0.00135941 14.6978 4.30753e-05 14.8492C-0.00127326 15.0007 0.0275921 15.1509 0.0849552 15.2911C0.142318 15.4314 0.22703 15.5587 0.334148 15.6658C0.441266 15.773 0.568645 15.8577 0.708852 15.915C0.849059 15.9724 0.999286 16.0013 1.15077 16C1.30225 15.9986 1.45195 15.9672 1.59114 15.9074C1.73033 15.8476 1.85622 15.7607 1.96146 15.6517L8 9.61317L14.0385 15.6517C14.2537 15.8595 14.5419 15.9745 14.841 15.9719C15.1401 15.9693 15.4263 15.8493 15.6378 15.6378C15.8493 15.4263 15.9693 15.1401 15.9719 14.841C15.9745 14.5419 15.8595 14.2537 15.6517 14.0385L9.61317 8Z"
            fill="#9CA3AF"
          />
        </svg>
      </div>
      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="text-3xl font-semibold text-[#1E429F]">
          Ready to download?
        </div>
        <div className="mt-6 text-center">
          You have requested transactions from{' '}
          <span className="font-semibold text-[#1C64F2]">
            {startDate ? dayjs(startDate).format('DD-MM-YYYY') : ''} to{' '}
            {endDate ? dayjs(endDate).format('DD-MM-YYYY') : ''}{' '}
          </span>
          for
          <div
            className="mb-6 px-6 font-semibold text-[#1C64F2]"
            style={{
              maxWidth: '100%',
              wordBreak: 'break-word', // Ensure long words break
              overflowWrap: 'break-word', // Break lines within long content
              overflow: 'hidden', // Prevent overflow
              textOverflow: 'ellipsis', // Add ellipsis for truncation
            }}
          >
            {displayNames}
          </div>
          <div className="inline-table w-[500px]">
            <Button
              loading={loading}
              text="Confirm & Download Report"
              onClick={handleDownload}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalContent;
